import { Observable } from 'rxjs';
import _ from 'lodash';
import type { Store } from 'redux';
import type { ActionsObservable } from 'redux-observable';

import VolunteersApi from 'entities/volunteers/api';
import DeedsApi from 'entities/deed/api';
import DonationsApi from 'entities/donation/api';
import SkillsApi from 'entities/skill/api';
import LocationApi from 'entities/location/api';
import VolunteerTimeApi from 'entities/volunteerTime/api';
import WalletTransactionApi from 'entities/walletTransaction/api';
import VolunteerTimeOffApi from 'entities/volunteerTimeOff/api';
import UserApi from 'entities/user/api';
import { selectUserLocations, selectUser, selectOrganization } from 'entities/user/selectors';
import { selectVolunteerById } from 'entities/volunteers/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectUpcomingDeedsLoaded, selectPastDeedsLoaded } from 'entities/deed/selectors';
import { selectDonationsLoaded } from 'entities/donation/selectors';
import { selectVolunteerTimesByUser } from 'entities/volunteerTime/selectors';
import { selectWalletTransactionsByUser } from 'entities/walletTransaction/selectors';
import { Privileges } from 'utils/privileges';
import { ADD } from 'entities/volunteers/constants';
import DonationScheduleApi from 'entities/donationSchedule/api';
import { Features } from 'utils/features';
import { showErrorAction } from 'containers/Notification/actions';
import { getAppDomain } from 'utils/getAppDomain';
import type { State } from 'reducers';

import {
  initSuccessAction,
  initFailedAction,
  fetchVolunteerTimeOffSummarySuccessAction,
  fetchVolunteerTimeOffSummaryFailedAction,
  fetchVolunteerTimeOffsSuccessAction,
  fetchVolunteerTimeOffsFailedAction,
  impersonateSuccessAction,
  impersonateFailedAction,
} from './actions';
import { INIT, IMPERSONATE } from './constants';

const init = (action$: ActionsObservable<{ type: typeof INIT; id: string }>, store: Store<State>) =>
  action$.ofType(INIT).exhaustMap(({ id }) => {
    const actions = [];
    const state = store.getState();
    const user = selectUser(state);
    const volunteer = selectVolunteerById(state, id);

    // formResponses are only available on the fully loaded user (not loaded from the volunteer list)
    if (!volunteer || !volunteer.formResponses) {
      actions.push(VolunteersApi.fetchById(id));
    }

    if (
      volunteer &&
      user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) &&
      user?.hasPrivilege(Privileges.VOLUNTEER_TIME_OFF_VIEW) &&
      user?.hasFeature(Features.VOLUNTEER_TIME_OFF)
    ) {
      actions.push(VolunteerTimeOffApi.fetchVolunteerTimeOffs(volunteer.id));
      actions.push(VolunteerTimeOffApi.fetchVolunteerTimeOffSummary(volunteer.id));
    }

    if (id && user?.hasPrivilege(Privileges.DONATION_SCHEDULE_VIEW)) {
      actions.push(
        DonationScheduleApi.getByFilter({
          userId: {
            equals: id,
          },
        })
      );
    }

    if (!selectUpcomingDeedsLoaded(state, id)) {
      actions.push(DeedsApi.fetchUpcoming(id));
    }

    if (!selectPastDeedsLoaded(state, id)) {
      actions.push(DeedsApi.fetchPast(id));
    }

    if (
      !selectDonationsLoaded(state, id) &&
      (!user?.organization.isCompany() ||
        (user?.hasFeature(Features.DONATIONS) && user?.hasPrivilege(Privileges.DONATION_VIEW)))
    ) {
      actions.push(DonationsApi.fetchDonations(id));
    }

    if (user?.hasPrivilege(Privileges.VOLUNTEERTIME)) {
      const volunteerTimes = selectVolunteerTimesByUser(state, id);
      if (volunteerTimes.size === 0) {
        actions.push(
          VolunteerTimeApi.getByFilter({
            userId: { equals: id },
            // @NOTE-DP: this filter doesn't provide any real security, but is here to make sure super admins see the same thing as regular admins
            [user?.organization?.isNonprofit() ? 'nonprofitId' : 'companyId']: {
              equals: user?.organization?.id,
            },
          })
        );
      }
    }

    if (
      user?.hasFeature(Features.DONATION_CREDIT) &&
      user?.hasPrivilege(Privileges.DONATION_VIEW)
    ) {
      const walletTransactions = selectWalletTransactionsByUser(state, id);
      if (walletTransactions.size === 0) {
        actions.push(WalletTransactionApi.getWalletTransactions(id));
      }
    }

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationApi.fetchAll());
    }

    const skills = selectSkills(state);
    if (skills.size === 0) {
      actions.push(SkillsApi.fetchAll());
    }

    if (actions.length === 0) {
      return Observable.of(initSuccessAction());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const fetchVolunteerTimeOffs = (
  action$: ActionsObservable<{ type: typeof ADD; volunteer: { id: string } }>,
  store: Store<State>
) =>
  action$.ofType(ADD).mergeMap(({ volunteer }) => {
    const state = store.getState();
    const user = selectUser(state);

    if (
      volunteer &&
      user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) &&
      user?.hasPrivilege(Privileges.VOLUNTEER_TIME_OFF_VIEW) &&
      user?.hasFeature(Features.VOLUNTEER_TIME_OFF)
    ) {
      return VolunteerTimeOffApi.fetchVolunteerTimeOffs(volunteer.id)
        .mergeMap((resultingAction) => [resultingAction, fetchVolunteerTimeOffsSuccessAction()])
        .catch((e) => Observable.of(fetchVolunteerTimeOffsFailedAction(e)));
    }

    return [];
  });

const updateVolunteerWithTimeOffSummary = (
  action$: ActionsObservable<{ type: typeof ADD; volunteer: { id: string } }>,
  store: Store<State>
) =>
  action$.ofType(ADD).mergeMap(({ volunteer }) => {
    const state = store.getState();
    const user = selectUser(state);

    if (
      volunteer &&
      user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) &&
      user?.hasPrivilege(Privileges.VOLUNTEER_TIME_OFF_VIEW) &&
      user?.hasFeature(Features.VOLUNTEER_TIME_OFF)
    ) {
      return VolunteerTimeOffApi.fetchVolunteerTimeOffSummary(volunteer.id)
        .mergeMap((resultingAction) => [
          resultingAction,
          fetchVolunteerTimeOffSummarySuccessAction(),
        ])
        .catch((e) => Observable.of(fetchVolunteerTimeOffSummaryFailedAction(e)));
    }

    return [];
  });

const impersonate = (
  action$: ActionsObservable<{
    type: typeof IMPERSONATE;
    userId: string;
    impersonationReason: string;
  }>,
  store: Store<State>
) =>
  action$.ofType(IMPERSONATE).mergeMap(({ userId, impersonationReason }) =>
    UserApi.impersonate(userId, impersonationReason)
      .mergeMap(({ token }) => {
        const organization = selectOrganization(store.getState());
        window.open(`${getAppDomain([organization])}/login/token#${token}`, '_blank');
        return [impersonateSuccessAction()];
      })
      .catch((e) =>
        Observable.of(
          impersonateFailedAction(e),
          showErrorAction('An error occurred during user impersonation, please try again.')
        )
      )
  );

export default [init, fetchVolunteerTimeOffs, updateVolunteerWithTimeOffSummary, impersonate];
